import { useState } from 'react';
import { MONTH_NAMES } from '../domain/age.js';
import { isValidPin } from '../lib/pin.js';

export const AVATARS = ['rocket', 'comet', 'moon', 'satellite', 'star', 'planet'];

const THIS_YEAR = new Date().getFullYear();

/**
 * Adding a child asks for a month and a year, and never for an age.
 *
 * Asking "how old are you?" is the friendlier question and the wrong one: the
 * answer is right for eleven months and then silently wrong, the raise stops
 * happening, and nothing on any screen indicates why. `domain/age.js` derives it.
 *
 * With `initial` it edits that child instead. The PIN field starts empty either
 * way — we only ever hold the hash — so in edit mode empty means "keep the one
 * they have" and removing it is a separate, deliberate tick.
 */
export default function ChildForm({ onSubmit, busy, initial, onCancel }) {
  const editing = Boolean(initial);
  const hasPin = Boolean(initial?.pin_hash);

  const [displayName, setName] = useState(initial?.display_name ?? '');
  const [avatar, setAvatar] = useState(initial?.avatar ?? AVATARS[0]);
  const [birthMonth, setMonth] = useState(initial?.birth_month ?? new Date().getMonth() + 1);
  const [birthYear, setYear] = useState(initial?.birth_year ?? THIS_YEAR - 8);
  const [pin, setPin] = useState('');
  const [removePin, setRemovePin] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (pin && !isValidPin(pin)) { setError('A PIN is four digits, or leave it empty.'); return; }
    setError(null);
    // undefined keeps the stored PIN; null clears it. See updateChild.
    const nextPin = editing ? (removePin ? null : (pin || undefined)) : (pin || null);
    await onSubmit({ displayName: displayName.trim(), avatar, birthMonth, birthYear, pin: nextPin });
    if (!editing) { setName(''); setPin(''); }
  }

  return (
    <form className="card stack" onSubmit={submit}>
      <label>
        Name
        <input value={displayName} onChange={(e) => setName(e.target.value)} required maxLength={40} />
      </label>

      <fieldset className="stack tight" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="small muted">Avatar</legend>
        <div className="row" role="radiogroup" aria-label="Avatar">
          {AVATARS.map((a) => (
            <button
              key={a} type="button" role="radio" aria-checked={avatar === a}
              className={avatar === a ? 'primary' : ''} onClick={() => setAvatar(a)}
            >
              {a}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="row">
        <label style={{ flex: '1 1 8rem' }}>
          Birth month
          <select value={birthMonth} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label style={{ flex: '1 1 6rem' }}>
          Birth year
          <input
            type="number" inputMode="numeric" value={birthYear}
            min={THIS_YEAR - 25} max={THIS_YEAR}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
      </div>
      <p className="small muted">
        Month and year only — enough to work out the allowance and time the birthday raise,
        and less than we’d be collecting with a full date of birth.
      </p>

      <label>
        {hasPin ? 'New PIN (leave empty to keep the current one)' : 'PIN (optional)'}
        <input
          type="text" inputMode="numeric" pattern="\d{4}" maxLength={4} value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} placeholder="1234"
          disabled={removePin}
        />
      </label>
      {hasPin && (
        <label className="row small muted" style={{ flexDirection: 'row' }}>
          <input
            type="checkbox" checked={removePin}
            onChange={(e) => { setRemovePin(e.target.checked); if (e.target.checked) setPin(''); }}
            style={{ width: 'auto', minHeight: 0 }}
          />
          Remove the PIN, so this profile opens without one
        </label>
      )}
      <p className="small muted">
        Four digits so a sibling doesn’t open the wrong profile. It isn’t a lock on the
        money — approving anything still needs you.
      </p>

      {error && <p className="notice error">{error}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy || !displayName.trim()}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Add to the crew'}
        </button>
        {editing && onCancel && (
          <button className="quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
        )}
      </div>
    </form>
  );
}
